// Avatar upload / remove for the profile card (profile spec §2.3, §5.1).
//
// The endpoints, the `Media.kind` invariant and the serving route already
// exist and are covered end-to-end by the backend suite; this is the missing
// half — the two buttons.
//
// Three things it deliberately does **not** do:
//
// * **No preview-before-upload.** The file is written on selection and the
//   card re-reads it. A local `URL.createObjectURL` preview would be nicer and
//   would also be a second source of truth for "what avatar do I have", which
//   is exactly the class of bug B2 was.
// * **No client-side type or size gate.** The media module owns the allowlist
//   (`image/jpeg|png|webp|heic`, 50 MB per w3.md §5.2 — W39.69 raised this from
//   8 MB) and profile spec §2.3 says to reuse it rather than invent a second
//   set. A second gate here would drift, and this one already did drift once
//   the media module moved.
// * **No drag-and-drop or camera capture.** Not in scope, and each would be
//   another untested path on iOS.

import { useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { Button } from "@/components/ui/button";
import { avatarUrl, deleteAvatar, uploadAvatar } from "@/lib/profile/api";
import { ApiError } from "@/lib/api/pair";
import { cn } from "@/lib/utils/cn";

export interface AvatarEditorProps {
  displayName: string;
  avatarUrlValue: string | null;
  tone: "you" | "partner";
  onChanged: () => void | Promise<void>;
}

export function AvatarEditor({
  displayName,
  avatarUrlValue,
  tone,
  onChanged,
}: AvatarEditorProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAvatar = Boolean(avatarUrlValue);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "頭像更新失敗, 請再試一次");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-s-2" data-testid="avatar-editor">
      <div className="flex items-center gap-s-4">
        <AuthorAvatar
          tone={tone}
          size="lg"
          label={displayName}
          src={avatarUrl(avatarUrlValue)}
        />
        <div className="flex flex-col gap-s-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            className="hidden"
            data-testid="avatar-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset first: re-picking the same file after a failure would
              // otherwise not fire `change` at all.
              e.target.value = "";
              if (file) void run(() => uploadAvatar(file));
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            data-testid="avatar-upload"
            onClick={() => inputRef.current?.click()}
          >
            <Camera size={16} strokeWidth={1.5} aria-hidden="true" className="mr-s-1" />
            {hasAvatar ? "更換頭像" : "上傳頭像"}
          </Button>
          {hasAvatar ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              data-testid="avatar-remove"
              onClick={() => void run(() => deleteAvatar())}
            >
              <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" className="mr-s-1" />
              移除
            </Button>
          ) : null}
        </div>
      </div>

      {!hasAvatar ? (
        <p className={cn("text-meta text-ink-3")} data-testid="avatar-fallback-note">
          沒有頭像時,會用「{displayName.slice(0, 1)}」的卡片代替。
        </p>
      ) : null}

      {error ? (
        <p className="text-meta text-partner-strong" role="alert" data-testid="avatar-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
